import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Button, buttonVariants, Input } from '@intelliauth/ui'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Shield,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { login as authLogin, CpV2ApiError, cpClient } from '@/lib/api/cpClient'

type InvitationPreview = components['schemas']['InvitationPreview']

function readErr(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, refreshSession } = useAuth()

  const [details, setDetails] = useState<InvitationPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Login form state
  const [showPassword, setShowPassword] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    setLoading(true)
    cpClient()
      .get('/api/v1/invitations/{token}', { path: { token } })
      .then((env) => {
        const data = (env as { data: InvitationPreview }).data
        setDetails(data)
      })
      .catch(() => setError('Invitation not found or invalid link.'))
      .finally(() => setLoading(false))
  }, [token])

  const handleLoginAndAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await authLogin({ email: loginEmail, password: loginPassword })
      await cpClient().post('/api/v1/invitations/{token}/accept', {
        path: { token },
      })
      await refreshSession()
      navigate('/dashboard')
    } catch (err) {
      setSubmitError(readErr(err, 'Failed to accept invitation'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAccept = async () => {
    if (!token) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await cpClient().post('/api/v1/invitations/{token}/accept', {
        path: { token },
      })
      await refreshSession()
      navigate('/dashboard')
    } catch (err) {
      setSubmitError(readErr(err, 'Failed to accept invitation'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Error fetching invitation
  if (error || !details) {
    return (
      <StatusCard
        tone="error"
        icon={XCircle}
        title="Invitation not found"
        description={error || 'This invitation link is invalid or has been removed.'}
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to login
          </Link>
        }
      />
    )
  }

  if (details.status === 'expired') {
    return (
      <StatusCard
        tone="warning"
        icon={Clock}
        title="Invitation expired"
        description={
          <>
            This invitation to join <strong>{details.org_name}</strong> has expired. Please ask the
            organization admin to send a new invitation.
          </>
        }
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to login
          </Link>
        }
      />
    )
  }

  if (details.status === 'revoked') {
    return (
      <StatusCard
        tone="error"
        icon={XCircle}
        title="Invitation revoked"
        description={
          <>
            This invitation to join <strong>{details.org_name}</strong> has been revoked by the
            organization admin.
          </>
        }
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to login
          </Link>
        }
      />
    )
  }

  if (details.status === 'accepted') {
    return (
      <StatusCard
        tone="success"
        icon={CheckCircle2}
        title="Already accepted"
        description={
          <>
            This invitation to join <strong>{details.org_name}</strong> has already been accepted.
          </>
        }
        action={
          <Link to="/dashboard" className={buttonVariants({ variant: 'primary' })}>
            Go to dashboard
          </Link>
        }
      />
    )
  }

  // Valid pending invitation
  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[60%] relative bg-gradient-to-br from-primary via-primary/80 to-sidebar overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative z-10 flex flex-col justify-between w-full p-8">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-8 w-8 text-white" />
            <span className="text-xl font-bold text-white">IntelliAuth</span>
          </Link>
          <div className="max-w-lg">
            <h1 className="text-4xl font-semibold text-white leading-tight">
              You've been invited!
            </h1>
            <p className="mt-4 text-lg text-white/70 leading-relaxed">
              Join <strong className="text-white">{details.org_name}</strong> as a{' '}
              <strong className="text-white">{details.role}</strong> on IntelliAuth.
            </p>
            <div className="mt-8 space-y-4">
              {[
                'Manage authentication flows visually',
                'Multi-tenant with full data isolation',
                'Enterprise-grade security',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3 text-white/80">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-sm text-white/40">Powered by IntelliAuth</div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <Shield className="h-10 w-10 text-primary" />
              <span className="text-2xl font-bold text-foreground">IntelliAuth</span>
            </Link>
          </div>

          {/* Invitation info banner */}
          <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <p className="text-sm text-primary">
              <Mail className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              You've been invited to join <strong>{details.org_name}</strong> as a{' '}
              <strong>{details.role}</strong>.
            </p>
          </div>

          {user ? (
            // Already logged in — just show accept button
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-foreground">Accept invitation</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  You're signed in as <strong>{user.email}</strong>
                </p>
              </div>

              {submitError && (
                <div className="flex items-start gap-3 p-4 bg-error-light border border-red-200 rounded-lg mb-6">
                  <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error-dark">{submitError}</p>
                </div>
              )}

              <Button
                variant="primary"
                size="md"
                onClick={handleAccept}
                loading={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2 justify-center">Accepting...</span>
                ) : (
                  'Accept Invitation'
                )}
              </Button>
            </>
          ) : (
            // Not logged in — show login form
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-foreground">Log in & accept</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Enter your credentials to join {details.org_name}
                </p>
              </div>

              <form onSubmit={handleLoginAndAccept} className="space-y-5">
                {submitError && (
                  <div className="flex items-start gap-3 p-4 bg-error-light border border-red-200 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error-dark">{submitError}</p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground mb-1.5"
                  >
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/80" />
                    <Input
                      id="email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10"
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-foreground mb-1.5"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pr-10"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/80 hover:text-muted-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  variant="primary"
                  size="md"
                  type="submit"
                  disabled={!loginEmail || !loginPassword}
                  loading={isSubmitting}
                  className="w-full"
                >
                  Log in & Accept
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 text-center text-xs text-muted-foreground/60">
            <a href="#" className="hover:text-muted-foreground/80">
              Terms of Service
            </a>{' '}
            &middot;{' '}
            <a href="#" className="hover:text-muted-foreground/80">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

const STATUS_TONE: Record<'error' | 'warning' | 'success', { bg: string; fg: string }> = {
  error: { bg: 'bg-destructive/10', fg: 'text-destructive' },
  warning: { bg: 'bg-ia-warning-bg', fg: 'text-ia-warning' },
  success: { bg: 'bg-ia-success-bg', fg: 'text-ia-success' },
}

function StatusCard({
  tone,
  icon: Icon,
  title,
  description,
  action,
}: {
  tone: 'error' | 'warning' | 'success'
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: React.ReactNode
  action: React.ReactNode
}) {
  const { bg, fg } = STATUS_TONE[tone]
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center">
        <div
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${bg}`}
        >
          <Icon className={`h-8 w-8 ${fg}`} />
        </div>
        <h1 className="text-2xl font-semibold text-foreground mb-2">{title}</h1>
        <p className="text-muted-foreground mb-8">{description}</p>
        {action}
      </div>
    </div>
  )
}
